import {
  Box,
  Collapse,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Toolbar,
  useTheme
} from '@mui/material';
import { Link, useLocation } from 'react-router-dom';
import React, { useState } from 'react';

import AssessmentIcon from '@mui/icons-material/Assessment';
import BarChartIcon from '@mui/icons-material/BarChart';
import BusinessIcon from '@mui/icons-material/Business';
import DashboardIcon from '@mui/icons-material/Dashboard';
import ExitToAppIcon from '@mui/icons-material/ExitToApp';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import GroupIcon from '@mui/icons-material/Group';
import LocationCityIcon from '@mui/icons-material/LocationCity';
import MapIcon from '@mui/icons-material/Map';
import PeopleIcon from '@mui/icons-material/People';
import PollIcon from '@mui/icons-material/Poll';
import QrCodeIcon from '@mui/icons-material/QrCode';
import SettingsIcon from '@mui/icons-material/Settings';
import SummarizeIcon from '@mui/icons-material/Summarize';
import SupervisorAccountIcon from '@mui/icons-material/SupervisorAccount';
import { Tv } from '@mui/icons-material';
import WavingHandIcon from '@mui/icons-material/WavingHand';
import useAuth from '../hooks/useAuth';

interface AppDrawerProps {
  drawerWidth: number;
  isLargeScreen: boolean;
  drawerOpen: boolean;
  setDrawerOpen: React.Dispatch<React.SetStateAction<boolean>>;
}

const AppDrawer: React.FC<AppDrawerProps> = ({ 
  drawerWidth, 
  isLargeScreen, 
  drawerOpen, 
  setDrawerOpen 
}) => {
  const location = useLocation();
  const theme = useTheme();

  const { user, hasPermissions } = useAuth();
  const [reportsOpen, setReportsOpen] = useState(false);
  const isAdmin = user?.permissions?.admin || false;

  const handleReportsToggle = () => {
    setReportsOpen(!reportsOpen);
  };

  const drawerContent = (
    <Box sx={{ width: drawerWidth }}>
      <Toolbar />
      <List>
        {hasPermissions && (
          <>
            {user?.permissions?.view_dashboard && (
              <ListItem disablePadding>
                <ListItemButton 
                  component={Link} 
                  to="/dashboard" 
                  selected={location.pathname === '/dashboard'}
                >
                  <ListItemIcon><DashboardIcon /></ListItemIcon>
                  <ListItemText primary="Dashboard" />
                </ListItemButton>
              </ListItem>
            )}
            {user?.permissions?.view_throwing && (
              <ListItem disablePadding>
                <ListItemButton 
                  component={Link} 
                  to="/throwing" 
                  selected={location.pathname === '/throwing'}
                >
                  <ListItemIcon><WavingHandIcon /></ListItemIcon>
                  <ListItemText primary="Throwing" />
                </ListItemButton>
              </ListItem>
            )}
            {user?.permissions?.view_exit_polls && (
              <ListItem disablePadding>
                <ListItemButton 
                  component={Link} 
                  to="/exit-polls" 
                  selected={location.pathname === '/exit-polls'}
                >
                  <ListItemIcon><ExitToAppIcon /></ListItemIcon>
                  <ListItemText primary="Exit Polls" />
                </ListItemButton>
              </ListItem>
            )}
            {(user?.permissions?.conduct_polls || user?.permissions?.manage_polls) && (
              <ListItem disablePadding>
                <ListItemButton 
                  component={Link} 
                  to="/polls"
                  selected={location.pathname === '/polls'}
                >
                  <ListItemIcon><PollIcon /></ListItemIcon>
                  <ListItemText primary="Polls" />
                </ListItemButton>
              </ListItem>
            )}
            {(user?.permissions?.view_voters || user?.permissions?.validate_polls) && (
              <ListItem disablePadding>
                <ListItemButton 
                  component={Link} 
                  to="/voters" 
                  selected={location.pathname === '/voters'}
                >
                  <ListItemIcon><PeopleIcon /></ListItemIcon>
                  <ListItemText primary="Voters" />
                </ListItemButton>
              </ListItem>
            )}
            {user?.permissions?.view_voter_groups && (
              <ListItem disablePadding>
                <ListItemButton 
                  component={Link} 
                  to="/voter-groups" 
                  selected={location.pathname === '/voter-groups'}
                >
                  <ListItemIcon><GroupIcon /></ListItemIcon>
                  <ListItemText primary="Voters Group" />
                </ListItemButton>
              </ListItem>
            )}
            {user?.permissions?.manage_organizations && (
              <ListItem disablePadding>
                <ListItemButton 
                  component={Link} 
                  to="/organizations" 
                  selected={location.pathname === '/organizations'}
                >
                  <ListItemIcon><BusinessIcon /></ListItemIcon>
                  <ListItemText primary="Organizations" />
                </ListItemButton>
              </ListItem>
            )}
            {user?.permissions?.view_precincts && (
              <ListItem disablePadding>
                <ListItemButton 
                  component={Link} 
                  to="/precincts" 
                  selected={location.pathname === '/precincts'}
                >
                  <ListItemIcon><MapIcon /></ListItemIcon>
                  <ListItemText primary="Precincts" />
                </ListItemButton>
              </ListItem>
            )}
            {user?.permissions?.view_barangays && (
              <ListItem disablePadding>
                <ListItemButton 
                  component={Link} 
                  to="/barangays" 
                  selected={location.pathname === '/barangays'}
                >
                  <ListItemIcon><LocationCityIcon /></ListItemIcon>
                  <ListItemText primary="Barangays" />
                </ListItemButton>
              </ListItem>
            )}
            {(user?.permissions?.view_responses || user?.permissions.conduct_polls) && (
              <ListItem disablePadding>
                <ListItemButton 
                  component={Link} 
                  to="/responses" 
                  selected={location.pathname === '/responses'}
                >
                  <ListItemIcon><AssessmentIcon /></ListItemIcon>
                  <ListItemText primary="Responses" />
                </ListItemButton>
              </ListItem>
            )}
            {user?.permissions?.view_reports && (
              <>
                <ListItem disablePadding>
                  <ListItemButton 
                    onClick={handleReportsToggle}
                    selected={location.pathname.startsWith('/reports')}
                  >
                    <ListItemIcon><SummarizeIcon /></ListItemIcon>
                    <ListItemText primary="Reports" />
                    {reportsOpen ? <ExpandLess /> : <ExpandMore />}
                  </ListItemButton>
                </ListItem>
                <Collapse in={reportsOpen} timeout="auto" unmountOnExit>
                  <List component="div" disablePadding>
                    <ListItem disablePadding sx={{ pl: 4 }}>
                      <ListItemButton
                        component={Link} 
                        to="/reports/voters/preference" 
                        selected={location.pathname === '/reports/voters/preference'}
                      >
                        <ListItemIcon><BarChartIcon /></ListItemIcon>
                        <ListItemText primary="Voters Preference" />
                      </ListItemButton>
                    </ListItem>
                    <ListItem disablePadding sx={{ pl: 4 }}>
                      <ListItemButton
                        component={Link} 
                        to="/reports/poll/responses" 
                        selected={location.pathname === '/reports/poll/responses'}
                      >
                        <ListItemIcon><BarChartIcon /></ListItemIcon>
                        <ListItemText primary="Poll Responses" />
                      </ListItemButton>
                    </ListItem>
                    <ListItem disablePadding sx={{ pl: 4 }}>
                      <ListItemButton
                        component={Link} 
                        to="/reports/final-list" 
                        selected={location.pathname === '/reports/final-list'}
                      >
                        <ListItemIcon><BarChartIcon /></ListItemIcon>
                        <ListItemText primary="Final List" />
                      </ListItemButton>
                    </ListItem>
                    {user?.permissions?.view_voter_groups && (
                      <>
                        <ListItem disablePadding sx={{ pl: 4 }}>
                          <ListItemButton
                            component={Link} 
                            to="/reports/voter-groups" 
                            selected={location.pathname === '/reports/voter-groups'}
                          >
                            <ListItemIcon><PeopleIcon /></ListItemIcon>
                            <ListItemText primary="Voters And Leaders" />
                          </ListItemButton>
                        </ListItem>
                        <ListItem disablePadding sx={{ pl: 4 }}>
                          <ListItemButton
                            component={Link} 
                            to="/reports/leaders" 
                            selected={location.pathname === '/reports/leaders'}
                          >
                            <ListItemIcon><PeopleIcon /></ListItemIcon>
                            <ListItemText primary="Leaders" />
                          </ListItemButton>
                        </ListItem>
                      </>
                    )}
                    {user?.permissions?.analytics && (
                      <>
                        <ListItem disablePadding sx={{ pl: 4 }}>
                          <ListItemButton
                            component={Link} 
                            to="/reports/survey" 
                            selected={location.pathname === '/reports/survey'}
                          >
                            <ListItemIcon><AssessmentIcon /></ListItemIcon>
                            <ListItemText primary="Survey" />
                          </ListItemButton>
                        </ListItem>
                        <ListItem disablePadding sx={{ pl: 4 }}>
                          <ListItemButton
                            component={Link} 
                            to="/reports/precinct-voters" 
                            selected={location.pathname === '/reports/precinct-voters'}
                          >
                            <ListItemIcon><PeopleIcon /></ListItemIcon>
                            <ListItemText primary="Precinct Voters" />
                          </ListItemButton>
                        </ListItem>
                      </>
                    )}
                  </List>
                </Collapse>
              </>
            )}
            {(user?.permissions?.scan_qr_stub_release || 
              user?.permissions?.scan_qr_final_release || 
              user?.permissions?.scan_qr_exit_poll) && (
              <ListItem disablePadding>
                <ListItemButton 
                  component={Link} 
                  to="/scan-purpose" 
                  selected={location.pathname === '/scan-purpose' || location.pathname === '/scan-voter-qr'}
                >
                  <ListItemIcon><QrCodeIcon /></ListItemIcon>
                  <ListItemText primary="Scan Voter QR" />
                </ListItemButton>
              </ListItem>
            )}
            {(isAdmin || user?.permissions?.manage_users) && (
              <ListItem disablePadding>
                <ListItemButton 
                  component={Link} 
                  to="/users" 
                  selected={location.pathname === '/users'}
                >
                  <ListItemIcon><SupervisorAccountIcon /></ListItemIcon>
                  <ListItemText primary="Users" />
                </ListItemButton>
              </ListItem>
            )}
            {(user?.permissions?.tv_monitor) && (
              <ListItem disablePadding>
                <ListItemButton 
                  component={Link} 
                  to="/tv" 
                  selected={location.pathname === '/tv'}
                >
                  <ListItemIcon><Tv /></ListItemIcon>
                  <ListItemText primary="TV Monitor" />
                </ListItemButton>
              </ListItem>
            )}
            {isAdmin && (
              <ListItem disablePadding>
                <ListItemButton 
                  component={Link} 
                  to="/utilities" 
                  selected={location.pathname === '/utilities'}
                >
                  <ListItemIcon><SettingsIcon /></ListItemIcon>
                  <ListItemText primary="Utilities" />
                </ListItemButton>
              </ListItem>
            )}
          </>
        )}
      </List>
    </Box>
  );

  return (
    <Box
      component="nav"
      sx={{
        width: { md: drawerWidth },
        flexShrink: { md: 0 },
        transition: theme.transitions.create('width', {
          easing: theme.transitions.easing.sharp,
          duration: theme.transitions.duration.enteringScreen,
        }),
        ...(location.pathname === '/tv' && {
          display: { md: 'none' },
        })
      }}
    >
      {isLargeScreen ? (
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', md: 'block' },
            '& .MuiDrawer-paper': { 
              boxSizing: 'border-box', 
              width: drawerWidth,
              transition: theme.transitions.create('width', {
                easing: theme.transitions.easing.sharp,
                duration: theme.transitions.duration.enteringScreen,
              }),
              ...(!drawerOpen && {
                overflowX: 'hidden',
                width: theme.spacing(7),
                [theme.breakpoints.up('sm')]: {
                  width: theme.spacing(9),
                },
              }),
            },
          }}
          open={drawerOpen}
        >
          {drawerContent}
        </Drawer>
      ) : (
        <Drawer
          variant="temporary"
          open={drawerOpen}
          onClose={() => setDrawerOpen(false)}
          ModalProps={{
            keepMounted: true, // Better open performance on mobile.
          }}
          sx={{
            display: { xs: 'block', md: 'none' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth },
          }}
        >
          {drawerContent}
        </Drawer>
      )}
    </Box>
  );
};

export default AppDrawer;
